import classNames from 'classnames';
import React from 'react';
import { FaEuroSign, FaRegTrashAlt } from 'react-icons/fa';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { MdAdd } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { Button } from 'rizzui';
import CustomSelectApiHookForm from '../../../components/forms/CustomSelectApiHookForms';
import RequiredInput from '../../../components/forms/InputHOC';
import useProducts from '../../../hooks/api-calls/useProducts';
import FormGroup from '../../../layout/shared/form-group';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';


interface ComplementsFormProps {
    formik: any;
    complement: { productId: string, name: string, amount: string };
    setComplement: (complement: { productId: string, name: string, amount: string }) => void;
}

const ComplementsForm: React.FC<ComplementsFormProps> = ({ formik, complement, setComplement }) => {

    const { id } = useParams<{ id: string }>();
    const { fetchProducts } = useProducts({ active: true, product: id });
    const { userCan } = usePrivilege();

    const canCreate = userCan('create_complements', 'complements');
    const canEdit   = userCan('edit_complements', 'complements');
    const canDelete = userCan('delete_complements', 'complements');

    return (
        <FormGroup
            title="Complementos"
            description="Productos asociados al producto principal"
            className='pt-6'
            titleCols="@md:col-span-2"
            childCols="@md:col-span-10"
        >
            <div className={classNames({ 'border-gray-500': formik.values.complements.length > 0 })}>
                {formik.values.complements.map((productItem: any, index: number) => {
                    return (
                        <div key={index} className="grid grid-cols-5 gap-4 mb-4">
                            <div className="col-span-2 space-y-2">
                                <CustomSelectApiHookForm
                                    id={`complements[${index}].productId`}
                                    label="Producto"
                                    required={formik.values.complements.length > 0}
                                    formik={formik}
                                    fetchOptions={async (searchText: string | null) => {
                                        const text = searchText || formik.values.complements[index]?.productName;
                                        return fetchProducts({ search_text: text }) as any;
                                    }}
                                    error={formik.errors.complements && formik.errors.complements[index]?.id}
                                    minInputLength={2}
                                    containerClassName=''
                                    isDisabled={!canEdit || !userCan('get_products_for_select', 'products')}
                                />
                                {!userCan('get_products_for_select', 'products') && (
                                    <div className="flex items-center gap-2 text-red-500 text-sm">
                                        <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                        <span>No tienes permisos para ver los productos</span>
                                    </div>
                                )}
                            </div>

                            <RequiredInput
                                required={formik.values?.complements?.length > 0}
                                id={`complements[${index}].name`}
                                label="Nombre"
                                formik={formik}
                                disabled={!canEdit}
                                value={productItem.name}
                                error={formik.errors.complements && formik.errors.complements[index]?.name}
                            />

                            <RequiredInput
                                required={formik.values.complements.length > 0}
                                id={`complements[${index}].amount`}
                                type="number"
                                label="Importe"
                                formik={formik}
                                disabled={!canEdit}
                                value={productItem.amount}
                                error={formik.errors.complements && formik.errors.complements[index]?.amount}
                                suffix={<FaEuroSign />}
                            />

                            {canDelete && (
                                <div className="flex justify-end items-end">
                                    <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => {
                                            const updatedProducts = formik.values.complements.filter(
                                                (_: any, i: number) => i !== index
                                            );
                                            formik.setFieldValue('complements', updatedProducts);
                                        }}
                                    >
                                        <FaRegTrashAlt />
                                    </Button>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {canCreate && (
                <div className='col-span-3'>
                    <div className="grid grid-cols-5 gap-4 mb-4">
                        <div className="col-span-2 space-y-2">
                            <CustomSelectApiHookForm
                                id='complementId'
                                label="Producto"
                                formik={formik}
                                fetchOptions={async (searchText: string | null) => {
                                    return fetchProducts(
                                        { 
                                            search_text: searchText, 
                                            exclude_ids: formik.values?.complements?.map((item: any) => item.productId) || []  
                                        }
                                    ) as any;
                                }}
                                setValue={(value: any) => { setComplement({ ...complement, productId: value.id, name: value.name , amount: value.object?.amount || '' }) }}
                                error={formik.errors.complements && formik.errors.complements[formik.values.complements.length]?.productId}
                                minInputLength={2}
                                containerClassName=''
                                isDisabled={!userCan('get_products_for_select', 'products')}
                            />
                            {!userCan('get_products_for_select', 'products') && (
                                <div className="flex items-center gap-2 text-red-500 text-sm">
                                    <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                    <span>No tienes permisos para ver los productos</span>
                                </div>
                            )}
                        </div>

                        <RequiredInput
                            required={false}
                            id='name'
                            label="Nombre"
                            formik={formik}
                            onChange={(e: any) => setComplement({ productId: complement.productId, name: e.target.value, amount: complement.amount })}
                            value={complement.name}
                        />

                        <RequiredInput
                            required={false}
                            id='amount'
                            type="number"
                            label="Importe"
                            suffix={<FaEuroSign />}
                            formik={formik}
                            onChange={(e: any) => setComplement({ productId: complement.productId, name: complement.name, amount: e.target.value })}
                            value={complement.amount}
                        />

                        <div className="flex justify-end items-end">
                            <Button
                                color='secondary'
                                size='sm'
                                disabled={complement.productId === '' || (formik.values.tutorType === 'external' && complement.amount === '')}
                                onClick={() => {
                                    formik.setFieldValue('complements', [...formik.values.complements, complement]);
                                    setComplement({ productId: '', name: '', amount: '' });
                                }}
                            >
                                <MdAdd className='h-5 w-5' />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </FormGroup>
    );
};

export default ComplementsForm;